import isString from 'lodash/isString';
import cloneDeep from 'lodash/cloneDeep';
import { YESTERDAY, DAYS_UNIT, HOURS_UNIT } from '@/assets/common/time-range-and-unit-values';

const defaultFormat = 'DD/MM/YYYY HH:mm:ss';

class DateHandlerService {
    constructor(dateAdapter) {
        this.dateAdapter = dateAdapter;

        this.range = this._rangeHelpers();

        this.timeZone = dateAdapter.tz.guess();
    }

    _rangeHelpers() {
        const getYesterdayRange = () => {
            const yesterday = this.dateAdapter.subtract(this.dateAdapter(), 1, DAYS_UNIT);

            return [
                this.dateAdapter.startOf(cloneDeep(yesterday), DAYS_UNIT),
                this.dateAdapter.endOf(cloneDeep(yesterday), DAYS_UNIT)
            ];
        };

        const debugDifference = (range, unit) => {
            return this.dateAdapter.unix(range[1]).diff(this.dateAdapter.unix(range[0]), unit);
        };

        return {
            normalize: range => range.map(date => this.normalizeDate(date)),
            toUtcUnix: range => range.map(date => this.dateAdapter(date).utc().unix()),
            getValueByOption: (option) => {
                if (option === YESTERDAY) {
                    return getYesterdayRange();
                }

                const splitOption = option.split('-');

                const [, amount, timeUnit] = splitOption;

                let now = this.dateAdapter();

                if (timeUnit === HOURS_UNIT) { now = this.dateAdapter.startOf(now, 'hour'); }
                if (timeUnit === DAYS_UNIT) { now = this.dateAdapter.startOf(now, 'day'); }

                let startDate = this.dateAdapter.subtract(
                    cloneDeep(now),
                    parseInt(amount),
                    timeUnit.endsWith('s') ? timeUnit.slice(0, -1) : timeUnit
                );

                if (timeUnit === HOURS_UNIT || timeUnit === DAYS_UNIT) { now = this.dateAdapter.subtract(now, 1, 'second'); }

                return [startDate, now];
            },
            getPreviousInterval: range => {
                const [startDate, endDate] = range;

                const differenceInMilliseconds = endDate.diff(startDate);

                const previousStartDate = this.dateAdapter.subtract(cloneDeep(startDate), differenceInMilliseconds, 'milliseconds');


                return [previousStartDate, startDate - 1];
            },

            debug: range => {
                const [unixStartDate, unixEndDate] = range;

                console.warn('-----RANGE DRILLDOWN-----');
                console.log(`from (UTC): ${this.dateAdapter.unix(unixStartDate).utc().format()}`);
                console.log(`to (UTC): ${this.dateAdapter.unix(unixEndDate).utc().format()}`);
                console.log('---');
                console.log(`from (local): ${this.dateAdapter(unixStartDate * 1000).format()}`);
                console.log(`to (local): ${this.dateAdapter(unixEndDate * 1000).format()}`);
                console.log('---');
                console.log(`number of days: ${debugDifference(range, DAYS_UNIT)}`);
                console.log(`number of hours: ${debugDifference(range, HOURS_UNIT)}`);
            },
            hasFullDay: range => {
                const [startDate, endDate] = range;

                const differenceInDays = this.dateAdapter
                    .unix(endDate)
                    .diff(this.dateAdapter.unix(startDate), 'days', true);

                return differenceInDays >= 1;
            },

            changeFormatOfString: (range, format, parseFormat) => {
                const [startDate, endDate] = range.split('-');
                const startDateFormatted = this.dateAdapter(startDate, parseFormat);
                const endDateFormatted = this.dateAdapter(endDate, parseFormat);
                console.log(startDateFormatted)
                const isDateValid = startDateFormatted.isValid() && endDateFormatted.isValid();

                if(!isDateValid) return range;

                return startDateFormatted.format(format) + ' - ' + endDateFormatted.format(format);

            }
        };
    }

    normalizeDate(date) {
        if (isString(date)) {
            date = this.dateAdapter(date);
        }

        const currentMinute = date.minute();

        if (!currentMinute) {
            return date.second(0);
        }

        if (currentMinute === 59) {
            return date.second(59);
        }

        return date;
    }

    dateToString(date, format = defaultFormat) {
        return this.dateAdapter(date).format(format);
    }

    stringToDate(value, format = defaultFormat) {
        return this.dateAdapter(value, format);
    }
}

export default DateHandlerService;
